<template>
  <!-- Empty state -->
  <a-card v-if="internalValue?.length === 0" class="empty-state">
    Aún no hay propiedades agregadas
  </a-card>

  <!-- Rows -->
  <template v-else>
    <div
      class="row"
      v-for="(item, index) in internalValue"
      :key="index"
    >
      <!-- Campo -->
      <a-form-item
        class="form-item"
        :name="['standard_properties', index, 'name']"
        :rules="[{ required: true, message: '' }]"
      >
        <template #label v-if="index === 0">
          Nombre del campo
          <a-popover overlayClassName="custom-pop">
            <template #content>
              <a-tabs v-model:activeKey="activeKeyName">
                <a-tab-pane key="1" tab="Params">
                  <p>
                    Es el nombre con el que la API reconocerá el dato al recibir la petición. 
                    Si tu tipo de envío es <span class="bold">Params</span> podrás encontrarlo en la URL de la solicitud.
                  </p>
                  <img width="440px" :src="imageParamsName" />
                </a-tab-pane>
                <a-tab-pane key="2" tab="Body" force-render>
                  <p>
                    Valor asignado al parámetro que se envía al servidor para filtrar o consultar datos específicos. 
                    Si tu tipo de envío es <span class="bold">Body</span> podrás encontrarlo al lado del nombre de tu propiedad.
                  </p>
                  <img width="440px" :src="imageBodyName" />
                </a-tab-pane>
              </a-tabs>
            </template>
            <InfoCircleOutlined style="margin: 0 3px" />
          </a-popover>
        </template>
        <a-input
          v-model:value="item.name"
          placeholder="Escribir el nombre"
          @input="emitUpdate"
        />
      </a-form-item>

      <!-- Valor -->
      <a-form-item
        class="form-item"
        :name="['standard_properties', index, 'value']"
        :rules="[{ required: true, message: '' }]"
      >
        <template #label v-if="index === 0">
          Valor
          <a-popover overlayClassName="custom-pop">
            <template #content>
              <a-tabs v-model:activeKey="activeKeyValue">
                <a-tab-pane key="1" tab="Params">
                  <p>
                    Valor asignado al parámetro que se envía al servidor para filtrar o consultar datos específicos. 
                    Si tu tipo de envío es <span class="bold">Params</span> podrás encontrarlo en la URL de la solicitud.
                  </p>
                  <img width="440px" :src="imageParamsValue" />
                </a-tab-pane>
                <a-tab-pane key="2" tab="Body" force-render>
                  <p>
                    Valor asignado al parámetro que se envía al servidor para filtrar o consultar datos específicos. 
                    Si tu tipo de envío es <span class="bold">Body</span> podrás encontrarlo al lado del nombre de tu propiedad.
                  </p>
                  <img width="440px" :src="imageBodyValue" />
                </a-tab-pane>
              </a-tabs>
            </template>
            <InfoCircleOutlined style="margin: 0 3px" />
          </a-popover>
        </template>
        <a-input
          v-model:value="item.value"
          placeholder="Escribir el valor"
          @input="emitUpdate"
        />
      </a-form-item>

      <!-- Tipo de envío -->
      <a-form-item
        class="form-item"
        :name="['standard_properties', index, 'send_type']"
        :rules="[{ required: true, message: '' }]"
      >
        <template #label v-if="index === 0">
          Tipo de envío
          <a-tooltip>
            <template #title>
              Especifica el tipo de valor que devuelve el campo de la API
            </template>
            <InfoCircleOutlined style="margin: 0 3px" />
          </a-tooltip>
        </template>
        <a-select
          v-model:value="item.send_type"
          :options="sendTypes"
          placeholder="Seleccionar una opción"
          @change="emitUpdate"
        />
      </a-form-item>

      <a-button
        type="text"
        danger
        @click="remove(index)"
        class="delete-btn"
      >
        <template #icon>
          <DeleteOutlined />
        </template>
      </a-button>
    </div>
  </template>

  <a-divider style="margin: 15px 0" />

  <div class="add-btn-wrapper">
    <a-button
      type="link"
      class="add-btn"
      :disabled="internalValue?.length >= max"
      @click="add"
    >
      + Agregar propiedad
    </a-button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { DeleteOutlined, InfoCircleOutlined } from "@ant-design/icons-vue";
import imageBodyName from "@/app/ivr/assets/body_name.png?url";
import imageParamsName from "@/app/ivr/assets/params_name.png?url";
import imageBodyValue from "@/app/ivr/assets/body_value.png?url";
import imageParamsValue from "@/app/ivr/assets/params_value.png?url";
import { sendTypes } from "../../utils/enums";

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  max: {
    type: Number,
    default: 5
  }
});

const emit = defineEmits(["update:modelValue"]);
const activeKeyName = ref('1');
const activeKeyValue = ref('1');

const internalValue = computed({
  get: () => props.modelValue,
  set: (val) => emit("update:modelValue", val),
});

const add = () => {
  if (internalValue.value?.length >= props.max) return;

  internalValue.value.push({
    name: "",
    value: "",
    send_type: null,
  });

  emitUpdate();
};

const remove = (index) => {
  internalValue.value.splice(index, 1);
  emitUpdate();
};

const emitUpdate = () => {
  emit("update:modelValue", internalValue.value);
};
</script>

<style lang="sass" scoped>
.header
  display: flex
  justify-content: space-between
  font-size: 13px
  margin-bottom: 12px

  .title
    font-weight: 600

  .limit
    font-size: 12px
    color: #8b8b8b

.empty-state
  font-size: 13px
  color: #8b8b8b
  :deep(.ant-card-body)
    padding: 8px
    text-align: center
    background-color: #00000005

.row
  display: grid
  grid-template-columns: 1.5fr 1fr 1fr 32px
  gap: 10px
  margin-bottom: 10px
  align-items: center

.form-item
  margin-bottom: 0

.name-input,
.value-input,
.select-input
  width: 100%

.delete-btn
  display: flex
  justify-content: center
  align-items: center

.add-btn-wrapper
  display: flex
  justify-content: center
  width: 100%
.add-btn
  padding-left: 0
  text-align: center

.custom-pop
  .ant-popover-inner
    width: 475px

.bold
  font-weight: 500
</style>
